import { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import * as PR from "../../prime-react/index";
import { getSavedFilters, createSavedFilter, updateSavedFilter, deleteSavedFilter} from "../../services/api";

export default function SavedFilters({ getCurrentFilters = () => ({}), preSelectedSavedFilters = () => { }, onResetFilters = () => { } , selectedFilter = null, screenType = 'alert' }) {
	const sessionData = useSelector((state) => state.auth.sessionData);
	const headers = useMemo(() => ({ sessionid: sessionData?.sessionId }), [sessionData]);
	const [value, setValue] = useState("");
	const [items, setItems] = useState([]);
	const [suggestions, setSuggestions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [msg, setMsg] = useState(null);
	const [errorType, setErrorType] = useState(null);
	const [autoCompleteKey, setAutoCompleteKey] = useState(0);

	const normalizeItem = (item) => {
		const id = item.filterID;
		const name = item.filterName;
		let filters = item.filterData ? JSON.parse(item.filterData) : {};
		return { id, name, filters };
	};

	const loadSaved = useCallback((showLoading = true) => {
		if (showLoading) setLoading(true);
		getSavedFilters(headers, null, (response) => {
			if (response.result === "SUCCESS") {
				const mapped = (response.data || []).map(normalizeItem).sort((a, b) => a.name.localeCompare(b.name));
				setItems(mapped);
			} else {
				setItems([]);
				setSuggestions([]);
			}
			setLoading(false);
		}, screenType);
	}, [headers, screenType]);
	useEffect(() => {
		if (selectedFilter?.id && items.length > 0) {
			const preSelected = items.find(f => f.id === selectedFilter.id);
			setValue(preSelected?.id ? preSelected : '');
		} else if(items.length > 0){
			setValue(null);//reset
			setAutoCompleteKey(prev => prev + 1);
		}
	}, [items, selectedFilter]);

	useEffect(() => {
		loadSaved(true);
	}, [loadSaved]);

	const search = (event) => {
		const query = event.query?.toLowerCase();
		if(!query){
			setSuggestions(items.map(i => ({ id: i.id, name: i.name, filters: i.filters })));
			return;
		}
		const filtered = (items || []).filter((item) => item.name?.toLowerCase().includes(query));
		setSuggestions(filtered);
	};

	const showMessage = (text, type = "success") => {
		setMsg(text);
		setErrorType(type);
		setTimeout(() => setMsg(null), 2500);
	};

	// Return true if `obj` contains at least one meaningful value.
	const hasFilters = (obj) => {
		return Object.keys(obj).some((key) => {
			const v = obj[key];
			if (v == null) return false;
			if (typeof v === 'string') return v.trim() !== '';
			if (Array.isArray(v)) return v.length > 0;
			if (typeof v === 'object') return hasFilters(v);
			return true;
		});
	};

	const handleSave = () => {
		const currentFilters = getCurrentFilters();
		if(screenType === 'report'){
			if(currentFilters.keyword){
				currentFilters.search = currentFilters.keyword;
				delete currentFilters.keyword;
			}
		 }
		if (!hasFilters(currentFilters)) return showMessage("No filters to save", 'warning');
		// update existing
		if (value && typeof value === "object" && value.id) {
			const payload = { name: value.name, filters: currentFilters };
			updateSavedFilter(value.id, payload, headers, null, (res) => {
				if (res.result === "SUCCESS") {
					showMessage("Filter successfully updated");
					// refresh and select updated item
					loadSaved();
				} else {
					showMessage(res.message || "Error encountered when trying to update the filter.", 'error');
				}
			}, screenType);
		} else {
			// create
			const name = typeof value === "string" ? value.trim() : value?.name?.trim();
			if (!name) return showMessage("Enter filter name");
			const payload = { name, filters: currentFilters };
			createSavedFilter(payload, headers, null, (res) => {
				if (res.result === "SUCCESS") {
					showMessage("Filter successfully saved");
					if (res.data) {
						const newFilter = { id: res.data, name: name, filters: currentFilters };
						handleSelectionChange({ value: newFilter });
						// add to lists
						setItems((prev) => [newFilter, ...prev]);
						setSuggestions((prev) => [newFilter, ...prev]);
					}
				} else {
					showMessage(res.message || "Error encountered when trying to save the filter", 'error');
				}
			}, screenType);
		}
	};

	const handleDelete = () => {
		let idToDelete = null;
		if (value && typeof value === "object" && value.id) idToDelete = value.id;
		
		if (!idToDelete) return showMessage("Select a saved filter to delete", 'warning');
		deleteSavedFilter(idToDelete, headers, null, (res) => {
			if (res.result === "SUCCESS") {
				showMessage("Filter successfully deleted");
				setValue("");
				onResetFilters(); // clear applied
				loadSaved();

			} else {
				showMessage(res.message || "Error encountered when trying to delete the filter", 'error');
			}
		});
	};


	const handleSelectionChange = (e) => {
		const val = e.value;
		setValue(val);
		if (!val.filters) return true;
		preSelectedSavedFilters(val)
	};

	return (
		<div className="card saved-filters-container">
			<div className="flex align-items-center">
				<label className="font-semibold">
					Saved Filter:
				</label>
				<div className="fields-container">
					<div className="flex gap-3 align-items-center">
						<PR.AutoComplete
						    key={autoCompleteKey}
							value={value}
							suggestions={suggestions}
							completeMethod={search}
							field="name"
							onChange={handleSelectionChange}
							emptyMessage="No Records found."
							showEmptyMessage={true}
							dropdown
							placeholder={loading ? "Loading…" : "Enter new filter name or choose a saved one"}
							onClear={onResetFilters}
						/>
						<div className="flex gap-2">
							<PR.Button
								label={value && typeof value === "object" && value.id ? "Update" : "Save"}
								icon="pi pi-save"
								outlined
								autoFocus
								className={'logout-btns click-btn color-update'}
								onClick={handleSave}
								disabled={value?.length < 3}
							/>
							<PR.Button
								icon="pi pi-trash"
								className="p-button-danger delete-btn"
								onClick={handleDelete}
								disabled={!value?.id}
							/>

						</div>
					</div>
					{msg && <div className={`${errorType}-msg`}>{msg}</div>}
				</div>
			</div>
		</div>
	);
}
